import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Settings, Globe, Bot, Mail, Shield } from "lucide-react";

import GeneralSettings from "./Tabs/GeneralSettings";
import AWSSettings from "./Tabs/AWSSettings";
import AzureSettings from "./Tabs/AzureSettings";
import GCPSettings from "./Tabs/GCPSettings";
import SaaSAppsSettings from "./Tabs/SaaSAppsSettings";
import LLMSettings from "./Tabs/LLMSettings";
import "./TenantSettings.css";

import { FaAws, FaGoogle } from "react-icons/fa6";
import { VscAzure } from "react-icons/vsc";
import { getCloudCredentialExpiry, useCloudCredentialStatuses } from "../../../services/useCloudCredentialStore";
import { CloudCredentialExpiredIcon } from "../CloudCredentialExpired";

interface TenantSetting {
    id: string;
    name: string;
    description: string;
    icon: any;
    configured: boolean;
    category: string;
    route?: string;
}

const CLOUD_BY_SETTING_ID: Record<string, string> = {
    "aws-setup": "aws",
    "azure-setup": "azure",
    "gcp-setup": "gcp",
};

const SettingsDetails: TenantSetting[] = [
    {
        id: "general-settings",
        name: "General Settings",
        description:
            "Configure basic tenant settings including email notification details, API tokens and preferences for your enterprise environment",
        icon: Settings,
        configured: true,
        category: "Configuration",
    },
    {
        id: "smtp-configuration",
        name: "SMTP Configuration",
        description:
            "Configure a custom SMTP server for outbound tenant emails. When not configured or disabled, SendGrid is used automatically",
        icon: Mail,
        configured: false,
        category: "Configuration",
        route: "/tenantSettings/smtp-configuration",
    },
    {
        id: "siem-connection",
        name: "SIEM Connection",
        description:
            "Forward security events to your SIEM over Syslog using CEF format. Configure destinations, transports, TLS, and filters",
        icon: Shield,
        configured: false,
        category: "Integrations",
        route: "/tenantSettings/siem-connection",
    },
    {
        id: "aws-setup",
        name: "AWS Setup",
        description:
            "Configure Amazon Web Services integration with IAM roles, S3 buckets, and EC2 instance management for cloud infrastructure",
        icon: FaAws,
        configured: false,
        category: "Cloud Platform",
    },
    {
        id: "azure-setup",
        name: "Azure Setup",
        description:
            "Set up Microsoft Azure integration including Active Directory, resource groups, and virtual machine management capabilities",
        icon: VscAzure,
        configured: true,
        category: "Cloud Platform",
    },
    {
        id: "gcp-setup",
        name: "GCP Setup",
        description:
            "Configure Google Cloud Platform services with project management, service accounts, and compute engine integration",
        icon: FaGoogle,
        configured: false,
        category: "Cloud Platform",
    },
    {
        id: "saas-apps",
        name: "SaaS Apps",
        description:
            "Connect and configure SaaS applications like GitLab to discover identities, permissions, and activity signals",
        icon: Globe,
        configured: false,
        category: "SaaS Platform",
    },
    {
        id: "llm-setup",
        name: "LLM Setup",
        description:
            "Configure AI model providers and enable prompt-backed automation for policies, alerts, summaries, and insights",
        icon: Bot,
        configured: false,
        category: "AI Platform",
    },
];

export default function TenantSettingDetails() {
    const navigate = useNavigate();
    const [activeSetting, setActiveSetting] = useState<string | null>(null);
    const credentialStatuses = useCloudCredentialStatuses();

    const renderActiveSetting = () => {
        switch (activeSetting) {
            case "general-settings":
                return <GeneralSettings onBack={() => setActiveSetting(null)} />;
            case "aws-setup":
                return <AWSSettings onBack={() => setActiveSetting(null)} />;
            case "azure-setup":
                return <AzureSettings onBack={() => setActiveSetting(null)} />;
            case "gcp-setup":
                return <GCPSettings onBack={() => setActiveSetting(null)} />;
            case "saas-apps":
                return <SaaSAppsSettings onBack={() => setActiveSetting(null)} />;
            case "llm-setup":
                return <LLMSettings onBack={() => setActiveSetting(null)} />;
            default:
                return null;
        }
    };

    const handleSettingClick = (setting: TenantSetting) => {
        if (setting.route) {
            navigate(setting.route);
            return;
        }
        setActiveSetting(setting.id);
    };

    if (activeSetting) return renderActiveSetting();

    return (
        <div className="tenant-settings-page flex-1 space-y-6 p-6">
            <Card className="settings-header-card border-border/40 rounded-2xl">
                <CardContent className="p-5 sm:p-6">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex items-center gap-4">
                            <div className="settings-icon-frame w-12 h-12 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-xl flex items-center justify-center border border-blue-200 dark:border-blue-700">
                                <Settings className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold tracking-tight" data-testid="text-page-title">
                                    Tenant Settings
                                </h1>
                                <p className="text-muted-foreground">
                                    Configure system settings and security options
                                </p>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {SettingsDetails.map((setting) => {
                    const IconComponent = setting.icon;
                    const cloud = CLOUD_BY_SETTING_ID[setting.id];
                    const credentialExpiry = cloud ? getCloudCredentialExpiry(credentialStatuses, cloud) : undefined;

                    return (
                        <Card
                            key={setting.id}
                            className="border-border/40 hover-elevate transition-all duration-200 cursor-pointer rounded-2xl"
                            onClick={() => handleSettingClick(setting)}
                            data-testid={`card-setting-${setting.id}`}
                        >
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="settings-icon-frame w-14 h-14 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-xl flex items-center justify-center border border-blue-200 dark:border-blue-700">
                                        <IconComponent className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    {credentialExpiry?.expired && (
                                        <CloudCredentialExpiredIcon reason={credentialExpiry.reason} />
                                    )}
                                    {/**
                                     * TODO - need to add the configure the status by the backend api
                                    <Badge
                                        variant="outline"
                                        className={`text-sm px-3 py-1 ${setting.configured
                                            ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800"
                                            : "bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-900/20 dark:text-gray-400 dark:border-gray-700"
                                            }`}
                                    >
                                        {setting.configured ? "Configured" : "Not Configured"}
                                    </Badge>
                                    */}
                                </div>

                                <div className="space-y-3">
                                    <h3 className="font-semibold text-lg leading-tight" data-testid={`title-${setting.id}`}>
                                        {setting.name}
                                    </h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">
                                        {setting.description}
                                    </p>
                                    <Badge variant="default" className="rounded-2xl text-sm font-normal">
                                        {setting.category}
                                    </Badge>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

        </div>
    );
}
